(() => {
  'use strict';

  const root = typeof window !== 'undefined' ? window : globalThis;

  function init() {
    const container = root.document.querySelector('.shopify-policy__container');
    const body = root.document.querySelector('.shopify-policy__body');

    // Shopify renders these pages with its own markup and no theme template, so
    // the layout is assembled here rather than in Liquid. Everything below is
    // additive: if the markup is not present, the page is left exactly as-is.
    if (!container || !body) {
      return;
    }

    container.classList.add('shopify-policy__container--enhanced');

    const headings = Array.from(body.querySelectorAll('h1, h2, h3'))
      .filter((heading) => heading.textContent.trim().length > 1);

    // The terms of service marks its sections with <p><strong>…</strong></p>
    // rather than real headings, so the longest policy would otherwise get no
    // contents list at all. A paragraph counts as a heading when bold is its
    // entire content and it reads like a label rather than a sentence.
    if (headings.length < 4) {
      Array.from(body.querySelectorAll('p')).forEach((para) => {
        const strong = para.firstElementChild;

        if (!strong || !/^(STRONG|B)$/.test(strong.tagName)) {
          return;
        }

        // The section title and its body share one paragraph, so the bold run
        // only has to *start* the paragraph -- requiring it to be the whole
        // paragraph matched nothing here.
        const label = strong.textContent.trim();

        if (label.length < 3 || label.length > 90) {
          return;
        }

        // A heading labels what follows; a bolded phrase mid-sentence does not.
        // Requiring the paragraph to begin with it, and the label not to end
        // like prose, keeps emphasis out of the contents list.
        if (!para.textContent.trim().startsWith(label) || /[.,;:]$/.test(label)) {
          return;
        }

        para.dataset.policyHeading = label;
        para.classList.add('shopify-policy__pseudo-heading');
        headings.push(para);
      });
    }

    // A contents list only earns its place on a long policy. The privacy policy
    // runs to ~2800 words; contact information is barely a screen and has short
    // headings that would otherwise qualify on count alone.
    const wordCount = body.textContent.trim().split(/\s+/).length;

    if (headings.length < 4 || wordCount < 400) {
      return;
    }

    headings.sort((a, b) => (
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    ));

    const nav = root.document.createElement('nav');
    nav.className = 'policy-toc';
    nav.setAttribute('aria-label', 'On this page');

    const title = root.document.createElement('p');
    title.className = 'policy-toc__title';
    title.textContent = 'On this page';
    nav.appendChild(title);

    const list = root.document.createElement('ol');
    list.className = 'policy-toc__list';

    const used = new Set();
    const links = [];

    headings.forEach((heading, index) => {
      let id = heading.id;

      if (!id) {
        id = (heading.dataset.policyHeading || heading.textContent)
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) || `section-${index}`;
      }

      // Merchant headings repeat -- several policies have more than one
      // "Contact information" -- so ids are made unique before use as anchors.
      let unique = id;
      let suffix = 2;
      while (used.has(unique)) {
        unique = `${id}-${suffix}`;
        suffix += 1;
      }
      used.add(unique);
      heading.id = unique;

      const item = root.document.createElement('li');
      item.className = heading.tagName === 'H3' ? 'policy-toc__item policy-toc__item--sub' : 'policy-toc__item';

      const link = root.document.createElement('a');
      link.className = 'policy-toc__link';
      link.href = `#${unique}`;
      link.textContent = heading.dataset.policyHeading || heading.textContent.trim();

      item.appendChild(link);
      list.appendChild(item);
      links.push({ link, heading });
    });

    nav.appendChild(list);
    container.insertBefore(nav, body);

    if (!root.IntersectionObserver) {
      return;
    }

    // Highlight whichever section is currently in view.
    const byId = new Map(links.map(({ link, heading }) => [heading.id, link]));
    const seen = new Set();

    const observer = new root.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          seen.add(entry.target.id);
        } else {
          seen.delete(entry.target.id);
        }
      });

      byId.forEach((link, id) => {
        link.classList.toggle('is-active', seen.has(id));
      });
    }, { rootMargin: '-10% 0px -70% 0px' });

    links.forEach(({ heading }) => observer.observe(heading));
  }

  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
